/**
 * Cash Wizard Content for Poker Reborn
 *
 * Content section for the Cash Games Custom Play wizard.
 * Provides a horizontal stakes slider (12 discrete levels) and seat toggle (6-max / 9-max).
 *
 * @see docs/screens/lobby/popups/WIZARD_CASH.md - Visual specs, dimensions, behavior
 * @see docs/screens/lobby/popups/CUSTOM_PLAY_WIZARD.md - Base wizard shell
 * @see src/utils/managerSelection.ts - Level data and manager lookup
 */

import { Container, Text, Graphics, FederatedPointerEvent } from 'pixi.js';
import { Button } from '@/components/common/Button';
import type { ManagerConfig } from '@/config/managers';
import { getAvailableLevels, findCashManager, formatChips } from '@/utils/managerSelection';
import type { LevelOption } from '@/utils/managerSelection';
import type { WizardContent } from './WizardContent';
import { WIZARD_CONTENT_STYLES } from './wizardStyles';

// =============================================================================
// SLIDER CONSTANTS
// =============================================================================

const SLIDER_TRACK_HEIGHT = 6;
const SLIDER_TRACK_RADIUS = 3;
const SLIDER_THUMB_RADIUS = 10;
const SLIDER_TICK_RADIUS = 2;

const SLIDER_TRACK_COLOR = 0x333344;
const SLIDER_FILL_COLOR = 0x2d8a27;
const SLIDER_THUMB_COLOR = 0xffffff;
const SLIDER_THUMB_HOVER_COLOR = 0xcccccc;
const SLIDER_THUMB_BORDER_COLOR = 0x000000;
const SLIDER_THUMB_BORDER_ALPHA = 0.3;
const SLIDER_THUMB_BORDER_WIDTH = 2;
const SLIDER_TICK_COLOR = 0x555566;

/** Height of the two-line info display above the slider */
const INFO_DISPLAY_HEIGHT = 34;
/** Gap between info display and slider track */
const INFO_TO_SLIDER_GAP = 12;
/** Gap between slider area and range labels */
const SLIDER_TO_RANGE_GAP = 4;
/** Height of range label text row */
const RANGE_LABELS_HEIGHT = 13;
/** Approximate height of a section label line */
const SECTION_LABEL_HEIGHT = 13;

// =============================================================================
// CASH WIZARD CONTENT
// =============================================================================

export class CashWizardContent implements WizardContent {
  readonly container: Container;

  private levels: readonly LevelOption[];
  private levelBuyins: readonly number[];
  private selectedLevel = 1;
  private selectedSeats: 6 | 9 = 6;
  private readinessCallback: ((ready: boolean) => void) | null = null;
  private contentWidth: number;

  // Slider elements
  private sliderContainer: Container;
  private fill: Graphics;
  private thumb: Graphics;
  private tierBlindsText: Text;
  private buyinText: Text;

  // Slider interaction state
  private isDragging = false;
  private isHovering = false;

  // Seat elements
  private seatButtons: Button[] = [];

  constructor(contentWidth: number) {
    this.contentWidth = contentWidth;
    this.container = new Container();
    this.levels = getAvailableLevels('cash');

    // Precompute max buy-in for each level (same for 6-max and 9-max)
    this.levelBuyins = this.levels.map((level) => {
      const manager = findCashManager(level.level, 6);
      return manager?.maxBuyin ?? 0;
    });

    let currentY = 0;

    // -- Section label: "Select Stakes:" --
    const stakesLabel = new Text({
      text: 'Select Stakes:',
      style: WIZARD_CONTENT_STYLES.sectionLabel,
    });
    stakesLabel.y = currentY;
    this.container.addChild(stakesLabel);
    currentY += SECTION_LABEL_HEIGHT + WIZARD_CONTENT_STYLES.gaps.sectionLabelToButtons;

    // -- Level info display (centered above slider) --
    this.tierBlindsText = new Text({
      text: '',
      style: {
        fill: 0xffffff,
        fontSize: 15,
        fontWeight: 'bold',
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    this.tierBlindsText.anchor.set(0.5, 0);
    this.tierBlindsText.x = contentWidth / 2;
    this.tierBlindsText.y = currentY;
    this.container.addChild(this.tierBlindsText);

    this.buyinText = new Text({
      text: '',
      style: {
        fill: 0x888899,
        fontSize: 12,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    this.buyinText.anchor.set(0.5, 0);
    this.buyinText.x = contentWidth / 2;
    this.buyinText.y = currentY + 18;
    this.container.addChild(this.buyinText);
    currentY += INFO_DISPLAY_HEIGHT + INFO_TO_SLIDER_GAP;

    // -- Stakes slider --
    this.sliderContainer = new Container();
    this.fill = new Graphics();
    this.thumb = new Graphics();
    this.sliderContainer.y = currentY;
    this.buildSlider();
    this.container.addChild(this.sliderContainer);
    currentY += SLIDER_THUMB_RADIUS * 2 + SLIDER_TO_RANGE_GAP;

    // -- Range labels --
    this.buildRangeLabels(currentY);
    currentY += RANGE_LABELS_HEIGHT + WIZARD_CONTENT_STYLES.gaps.sectionToSection;

    // -- Section label: "Select Seats:" --
    const seatsLabel = new Text({
      text: 'Select Seats:',
      style: WIZARD_CONTENT_STYLES.sectionLabel,
    });
    seatsLabel.y = currentY;
    this.container.addChild(seatsLabel);
    currentY += SECTION_LABEL_HEIGHT + WIZARD_CONTENT_STYLES.gaps.sectionLabelToButtons;

    // -- Seat toggle buttons --
    this.buildSeatButtons(currentY);

    // Initial draw
    this.updateInfoDisplay();
    this.drawFill();
    this.drawThumb();
  }

  getContentHeight(): number {
    const { sectionLabelToButtons, sectionToSection, seatButtonHeight } = WIZARD_CONTENT_STYLES.gaps;
    return (
      SECTION_LABEL_HEIGHT + sectionLabelToButtons +
      INFO_DISPLAY_HEIGHT + INFO_TO_SLIDER_GAP +
      SLIDER_THUMB_RADIUS * 2 + SLIDER_TO_RANGE_GAP +
      RANGE_LABELS_HEIGHT + sectionToSection +
      SECTION_LABEL_HEIGHT + sectionLabelToButtons +
      seatButtonHeight
    );
  }

  isReady(): boolean {
    return true; // Slider always has a valid selection
  }

  getSelectedManager(): ManagerConfig | undefined {
    return findCashManager(this.selectedLevel, this.selectedSeats);
  }

  onReadinessChange(callback: (ready: boolean) => void): void {
    this.readinessCallback = callback;
    // Defer so the caller finishes construction before the callback fires
    queueMicrotask(() => { callback(true); });
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }

  // ===========================================================================
  // SLIDER BUILDING
  // ===========================================================================

  private buildSlider(): void {
    const trackCenterY = SLIDER_THUMB_RADIUS;

    // Track background
    const track = new Graphics();
    track.roundRect(
      0,
      trackCenterY - SLIDER_TRACK_HEIGHT / 2,
      this.contentWidth,
      SLIDER_TRACK_HEIGHT,
      SLIDER_TRACK_RADIUS,
    );
    track.fill(SLIDER_TRACK_COLOR);
    track.eventMode = 'none';
    this.sliderContainer.addChild(track);

    // Fill (green portion left of thumb, redrawn on selection change)
    this.fill.eventMode = 'none';
    this.sliderContainer.addChild(this.fill);

    // Tick marks (on top of fill so they're visible at all positions)
    const tickMarks = new Graphics();
    for (let i = 0; i < this.levels.length; i++) {
      tickMarks.circle(this.getStopX(i), trackCenterY, SLIDER_TICK_RADIUS);
    }
    tickMarks.fill(SLIDER_TICK_COLOR);
    tickMarks.eventMode = 'none';
    this.sliderContainer.addChild(tickMarks);

    // Thumb (redrawn on selection change)
    this.thumb.eventMode = 'none';
    this.sliderContainer.addChild(this.thumb);

    // Interaction — entire slider area is draggable/tappable
    this.sliderContainer.eventMode = 'static';
    this.sliderContainer.cursor = 'pointer';
    const hitPadding = 10;
    this.sliderContainer.hitArea = {
      contains: (x: number, y: number) => {
        return (
          x >= -hitPadding &&
          x <= this.contentWidth + hitPadding &&
          y >= trackCenterY - SLIDER_THUMB_RADIUS - hitPadding &&
          y <= trackCenterY + SLIDER_THUMB_RADIUS + hitPadding
        );
      },
    };

    this.sliderContainer.on('pointerdown', (event: FederatedPointerEvent) => {
      this.isDragging = true;
      this.updateSliderFromPointer(event);
    });
    this.sliderContainer.on('pointermove', (event: FederatedPointerEvent) => {
      if (!this.isDragging) return;
      this.updateSliderFromPointer(event);
    });
    this.sliderContainer.on('pointerup', () => {
      this.isDragging = false;
      this.drawThumb();
    });
    this.sliderContainer.on('pointerupoutside', () => {
      this.isDragging = false;
      this.drawThumb();
    });
    this.sliderContainer.on('pointerover', () => {
      this.isHovering = true;
      this.drawThumb();
    });
    this.sliderContainer.on('pointerout', () => {
      this.isHovering = false;
      this.drawThumb();
    });
  }

  // ===========================================================================
  // SLIDER DRAWING
  // ===========================================================================

  private getStopX(levelIndex: number): number {
    const stopCount = this.levels.length;
    if (stopCount <= 1) return 0;
    return (levelIndex / (stopCount - 1)) * this.contentWidth;
  }

  private drawFill(): void {
    const trackCenterY = SLIDER_THUMB_RADIUS;
    const levelIndex = this.selectedLevel - 1;
    const fillWidth = this.getStopX(levelIndex);

    this.fill.clear();
    if (fillWidth > 0) {
      this.fill.roundRect(
        0,
        trackCenterY - SLIDER_TRACK_HEIGHT / 2,
        fillWidth,
        SLIDER_TRACK_HEIGHT,
        SLIDER_TRACK_RADIUS,
      );
      this.fill.fill(SLIDER_FILL_COLOR);
    }
  }

  private drawThumb(): void {
    const trackCenterY = SLIDER_THUMB_RADIUS;
    const levelIndex = this.selectedLevel - 1;
    const thumbX = this.getStopX(levelIndex);
    const thumbColor = this.isHovering || this.isDragging
      ? SLIDER_THUMB_HOVER_COLOR
      : SLIDER_THUMB_COLOR;

    this.thumb.clear();
    this.thumb.circle(thumbX, trackCenterY, SLIDER_THUMB_RADIUS);
    this.thumb.fill(thumbColor);
    this.thumb.circle(thumbX, trackCenterY, SLIDER_THUMB_RADIUS);
    this.thumb.stroke({
      color: SLIDER_THUMB_BORDER_COLOR,
      width: SLIDER_THUMB_BORDER_WIDTH,
      alpha: SLIDER_THUMB_BORDER_ALPHA,
    });
  }

  private updateInfoDisplay(): void {
    const levelIndex = this.selectedLevel - 1;
    const level = this.levels[levelIndex];
    if (!level) return;

    this.tierBlindsText.text = `${level.tier} — ${level.label}`;

    const maxBuyin = this.levelBuyins[levelIndex];
    this.buyinText.text = maxBuyin !== undefined
      ? `Buy-In: ${formatChips(maxBuyin)}`
      : '';
  }

  private updateSliderFromPointer(event: FederatedPointerEvent): void {
    const local = this.sliderContainer.toLocal(event.global);
    const ratio = Math.max(0, Math.min(1, local.x / this.contentWidth));
    const levelIndex = Math.round(ratio * (this.levels.length - 1));
    const newLevel = levelIndex + 1;

    if (newLevel !== this.selectedLevel) {
      this.selectedLevel = newLevel;
      this.updateInfoDisplay();
      this.drawFill();
      this.drawThumb();
    }
  }

  // ===========================================================================
  // RANGE LABELS
  // ===========================================================================

  private buildRangeLabels(startY: number): void {
    const firstLevel = this.levels[0];
    const lastLevel = this.levels[this.levels.length - 1];
    const rangeLabelStyle = {
      fill: 0x666677,
      fontSize: 11,
      fontFamily: 'Roboto, Arial, sans-serif',
    };

    if (firstLevel) {
      const leftLabel = new Text({
        text: firstLevel.tier,
        style: rangeLabelStyle,
      });
      leftLabel.y = startY;
      this.container.addChild(leftLabel);
    }

    if (lastLevel) {
      const rightLabel = new Text({
        text: lastLevel.tier,
        style: rangeLabelStyle,
      });
      rightLabel.anchor.set(1, 0);
      rightLabel.x = this.contentWidth;
      rightLabel.y = startY;
      this.container.addChild(rightLabel);
    }
  }

  // ===========================================================================
  // SEAT BUTTONS
  // ===========================================================================

  private buildSeatButtons(startY: number): void {
    const { seatButtonHeight, seatButtonGap } = WIZARD_CONTENT_STYLES.gaps;
    const buttonWidth = (this.contentWidth - seatButtonGap) / 2;

    const sixMaxButton = new Button({
      label: '6-max',
      width: buttonWidth,
      height: seatButtonHeight,
      style: WIZARD_CONTENT_STYLES.seatButtonSelected, // Default selected
    });
    sixMaxButton.y = startY;
    sixMaxButton.onClick = () => { this.selectSeats(6); };
    this.container.addChild(sixMaxButton);
    this.seatButtons.push(sixMaxButton);

    const nineMaxButton = new Button({
      label: '9-max',
      width: buttonWidth,
      height: seatButtonHeight,
      style: WIZARD_CONTENT_STYLES.seatButton, // Unselected
    });
    nineMaxButton.x = buttonWidth + seatButtonGap;
    nineMaxButton.y = startY;
    nineMaxButton.onClick = () => { this.selectSeats(9); };
    this.container.addChild(nineMaxButton);
    this.seatButtons.push(nineMaxButton);
  }

  private selectSeats(seats: 6 | 9): void {
    this.selectedSeats = seats;

    if (this.seatButtons.length === 2) {
      const [sixButton, nineButton] = this.seatButtons as [Button, Button];
      if (seats === 6) {
        sixButton.setStyle(WIZARD_CONTENT_STYLES.seatButtonSelected);
        nineButton.setStyle(WIZARD_CONTENT_STYLES.seatButton);
      } else {
        sixButton.setStyle(WIZARD_CONTENT_STYLES.seatButton);
        nineButton.setStyle(WIZARD_CONTENT_STYLES.seatButtonSelected);
      }
    }
  }
}
